import classNames from 'classnames';
import React, { useState } from 'react';
import {
  GetTransactionsPayload,
  TransactionAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { defaultCurrencyImage, escape, getLocalizedProperty } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { stringify } from 'qs';
import { useTranslation } from 'react-i18next';
import { GenericComponent } from '../../components-tree';
import { ReactBootstrap } from '../../ReactBootstrap';
import {
  TableSkeletonRows,
  TransactionHistoryTableV2Skeleton,
} from './TransactionHistoryTableV2Skeleton';
import { TransactionHistoryTablePage } from './TransactionHistoryTablePage';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';
import { queryClient } from '../../utils/query-client';

async function getTransactions(
  programId: string,
  currencyIds: string[],
  pageNumber: number,
): Promise<GetTransactionsPayload> {
  const qParams = stringify({ pageNumber, currencyIds }, { addQueryPrefix: true });
  const rowData = await fetch(`/l/${programId}/api/transactions${qParams}`);
  return rowData.json();
}

export const TRANSACTION_HISTORY_TABLE_V2_CLASSNAME = 'qlfeditor-achievements-history-table';

export interface TransactionTableV2Attributes {
  currencyIds?: string[];
}
type ComponentDefinition = GenericComponent<TransactionTableV2Attributes>;
type BackendProps = WidgetProps<ComponentDefinition>;
interface Props {
  programId: string;
  attributes: ComponentDefinition['attributes'];
  currencies: { [id: string]: { name: string; image: string } };
  id: string;
  locale: string;
  transactionsFixture?: TransactionAttributes[]; // defined only inside the editor
}

export function serializeTransactionTableV2Props({ state, options, context }: BackendProps): Props {
  const { currencies: rawCurrencies, transactionsFixture, programId } = state;
  const { locale } = context.i18n;

  const selectedCurrencyIds = options.attributes.currencyIds;

  const currencies = Object.fromEntries(
    rawCurrencies
      .filter(c => !selectedCurrencyIds || selectedCurrencyIds.includes(c.id))
      .map(currency => [
        currency.id,
        {
          image: escape(getLocalizedProperty(currency, 'image', locale)) || defaultCurrencyImage,
          name: escape(getLocalizedProperty(currency, 'name', locale)) || 'Points',
        },
      ]),
  );

  // Mirror portal API filtering: editor fixture includes all currencies,
  // so keep only transactions for the selected currency set.
  // Retro-compatibility: undefined currencyIds means no filtering.
  const filteredTransactionsFixture = selectedCurrencyIds
    ? transactionsFixture?.filter(transaction =>
        selectedCurrencyIds.includes(transaction.currencyId),
      )
    : transactionsFixture;

  return {
    attributes: options.attributes,
    currencies,
    id: options.id,
    locale,
    transactionsFixture: filteredTransactionsFixture,
    programId,
  };
}

export function TransactionHistoryTableV2Component(props: Props) {
  return (
    <TranslationProvider loader={<TransactionHistoryTableV2Skeleton />}>
      <QueryClientProvider client={queryClient}>
        <Component {...props} />
      </QueryClientProvider>
    </TranslationProvider>
  );
}

function Component(props: Props) {
  const { id, attributes, locale, transactionsFixture, programId, currencies } = props;
  const { t } = useTranslation('participant');
  const [currentPage, setCurrentPage] = useState(0);
  const currencyIds = Object.keys(currencies).sort();

  const transactions = useQuery({
    queryKey: ['transactions', programId, currencyIds, currentPage],

    queryFn: async () => {
      return getTransactions(programId, currencyIds, currentPage);
    },

    initialData: transactionsFixture
      ? { data: transactionsFixture, isLast: true, pageCount: 1 }
      : undefined,

    enabled: !transactionsFixture,
  });

  return (
    <div
      className={classNames(
        TRANSACTION_HISTORY_TABLE_V2_CLASSNAME,
        'transactions-table',
        attributes.class,
      )}
    >
      <table className="qlf-table">
        <caption className="sr-only">{t('aria.transactionHistory.table.caption')}</caption>
        <thead>
          <tr>
            <th scope="col" className="date">
              <i className="fas fa-calendar" aria-hidden />
              <span style={{ marginLeft: 5 }}>
                {t('editor.achievements-history.table.columns.date')}
              </span>
            </th>
            <th scope="col" className="label">
              {t('editor.achievements-history.table.columns.label')}
            </th>
            <th scope="col" className="points">
              <i
                className="fas fa-trophy-alt"
                aria-label={t('aria.transactionHistory.table.columns.points')}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {transactions.isPending ? (
            <TableSkeletonRows />
          ) : transactions.isError ? (
            <div role="alert" className="sr-only">
              {t('aria.transactionHistory.pageLoadError', { pageNumber: currentPage + 1 })}
            </div>
          ) : !transactions.data?.data?.length ? (
            // FIXME empty state should not be inside table body
            <div style={{ textAlign: 'center' }}>
              <div role="alert" className="sr-only">
                {t('aria.transactionHistory.pageLoaded', { pageNumber: currentPage + 1 })}
              </div>
              <p>{t('editor.achievements.list.empty')}</p>
              <p aria-hidden>
                <i className="fal fa-medal fa-5x" style={{ marginBottom: 0 }}></i>
              </p>
            </div>
          ) : (
            <>
              <div role="alert" className="sr-only">
                {t('aria.transactionHistory.pageLoaded', { pageNumber: currentPage + 1 })}
              </div>

              <TransactionHistoryTablePage
                transactions={transactions.data?.data || []}
                currencies={currencies}
                locale={locale}
              />
            </>
          )}
        </tbody>
        {transactions?.data && transactions.data.pageCount > 1 && (
          <tfoot>
            <tr>
              <td>
                <div className="pagination qlfeditor-pagination--section__cta">
                  <button
                    className="qlfeditor-pagination--button__cta"
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    disabled={currentPage === 0 || transactions.isPending}
                    aria-label={t('aria.previousPage')}
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  <div className="qlfeditor--pagination__cta">
                    {t('widgets.transactionHistory.pagination.position', {
                      current: (currentPage + 1).toFixed(0),
                      total: transactions.data.pageCount == 0 ? 1 : transactions.data.pageCount,
                    })}
                  </div>
                  <button
                    className="qlfeditor-pagination--button__cta"
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={transactions.data.isLast || transactions.isPending}
                    aria-label={t('aria.nextPage')}
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export function TransactionHistoryTableV2Renderer(props: BackendProps, document?: Document) {
  const reactProps = serializeTransactionTableV2Props(props);
  return renderToString(
    <ReactBootstrap
      component="TransactionHistoryTableV2Component"
      props={reactProps}
      document={document}
      componentType="transaction-history-table-v2"
    />,
  );
}
